import {
  AnalyticsResult,
  CategorySales,
  ChannelPerformance,
  ChannelSales,
  DeadStockItem,
  DohItem,
  LowStockItem,
  Order,
  OrderStatus,
  SalesPoint,
  StatusCount,
  TopCustomer,
  TopProduct,
} from "../models/analytics";
import { DateRange } from "../models/filters";

const isQualifying = (status: Order["status"]) =>
  status === "UNFULFILLED" || status === "PARTIALLY_FULFILLED" || status === "FULFILLED";

function dateRangeDays(range: DateRange): string[] {
  const start = new Date(range.from + "T00:00:00.000Z");
  const end = new Date(range.to + "T00:00:00.000Z");
  const days: string[] = [];

  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    days.push(d.toISOString().substring(0, 10));
  }

  return days;
}

export function aggregateAnalytics(orders: Order[], range: DateRange): AnalyticsResult {
  const qualifying = orders.filter((o) => isQualifying(o.status));

  const gmv = qualifying.reduce((sum, o) => sum + o.gross, 0);
  const ordersCount = qualifying.length;

  const byDate = new Map<string, number>();

  for (const o of qualifying) {
    byDate.set(o.created, (byDate.get(o.created) ?? 0) + o.gross);
  }

  const days = dateRangeDays(range);
  const salesSeries: SalesPoint[] = days.map((d) => ({ date: d, value: +(byDate.get(d) ?? 0).toFixed(2) }));

  // Deterministic RNG seeded by date range
  const seedBase =
    (parseInt((range.from || "").replace(/-/g, ""), 10) || 0) * 31 +
    (parseInt((range.to || "").replace(/-/g, ""), 10) || 0);
  const rand = mulberry32(seedBase || 123456);

  // Orders by status (all orders, not only qualifying)
  const allStatuses: OrderStatus[] = ["UNFULFILLED", "PARTIALLY_FULFILLED", "FULFILLED", "CANCELED"];
  const statusCountMap = new Map<OrderStatus, number>();

  for (const s of allStatuses) statusCountMap.set(s, 0);
  for (const o of orders) statusCountMap.set(o.status, (statusCountMap.get(o.status) || 0) + 1);

  const statusBreakdown: StatusCount[] = allStatuses.map((s) => ({ status: s, count: statusCountMap.get(s) || 0 }));
  const totalOrdersAll = orders.length || 1;
  const cancellationsCount = statusCountMap.get("CANCELED") || 0;
  const cancellationsRate = +(cancellationsCount / totalOrdersAll).toFixed(4);

  // Impacts and rates (mocked deterministically)
  const refundRate = +(0.02 + rand() * 0.06).toFixed(4);
  const voucherImpact = +(gmv * (0.02 + rand() * 0.03)).toFixed(2);
  const giftCardImpact = +(gmv * (0.01 + rand() * 0.02)).toFixed(2);
  const refundsAmount = +(gmv * refundRate).toFixed(2);
  const netSales = Math.max(0, +(gmv - voucherImpact - giftCardImpact - refundsAmount).toFixed(2));

  const avgOrderValue = ordersCount > 0 ? +(gmv / ordersCount).toFixed(2) : 0;
  const avgItemsPerOrder = +(1.2 + rand() * 2.2).toFixed(2);

  // Best sellers (Top 10)
  const bestSellers: TopProduct[] = Array.from({ length: 10 }).map((_, i) => {
    const units = 15 + Math.floor(rand() * 120);
    const price = 10 + Math.round(rand() * 90);

    return { name: `Product ${String.fromCharCode(65 + i)}`, units, gmv: +(units * price).toFixed(2) };
  });

  // Sales by channel & category (mock split of GMV)
  const splits = normalizeToOne([rand(), rand(), rand() * 0.5]);
  const salesByChannel: ChannelSales[] = [
    { channel: "Online", currency: "USD", gmv: +(gmv * splits[0]).toFixed(2) },
    { channel: "Retail", currency: "USD", gmv: +(gmv * splits[1]).toFixed(2) },
    { channel: "Wholesale", currency: "USD", gmv: +(gmv * splits[2]).toFixed(2) },
  ];
  const channelOrderSplits = normalizeToOne([rand(), rand(), rand() * 0.5]);
  const channelPerformance: ChannelPerformance[] = salesByChannel.map((c, i) => {
    const orders = Math.max(1, Math.round(ordersCount * channelOrderSplits[i]));
    const totalGmv = salesByChannel.reduce((s, x) => s + x.gmv, 0) || 1;

    return {
      channel: c.channel,
      currency: c.currency,
      gmv: c.gmv,
      orders,
      aov: +(c.gmv / orders).toFixed(2),
      share: +(c.gmv / totalGmv).toFixed(4),
      refundRate: +(rand() * 0.06).toFixed(4),
    };
  });
  const catSplits = normalizeToOne([rand(), rand(), rand(), rand() * 0.6]);
  const salesByCategory: CategorySales[] = [
    { category: "Tops", gmv: +(gmv * catSplits[0]).toFixed(2) },
    { category: "Shoes", gmv: +(gmv * catSplits[1]).toFixed(2) },
    { category: "Accessories", gmv: +(gmv * catSplits[2]).toFixed(2) },
    { category: "Outerwear", gmv: +(gmv * catSplits[3]).toFixed(2) },
  ];

  // Customers
  const topCustomers: TopCustomer[] = Array.from({ length: 10 }).map((_, i) => {
    const orders = 1 + Math.floor(rand() * 9);
    const spend = +(orders * (30 + rand() * 170)).toFixed(2);

    return { customer: `Customer ${String(i + 1).padStart(3, "0")}`, orders, gmv: spend };
  });
  const repeatRate = +(0.25 + rand() * 0.35).toFixed(4);
  const timeToSecondOrderDays = Math.round(18 + rand() * 24);
  const churnRiskRate = +(0.06 + rand() * 0.09).toFixed(4);

  // Inventory (threshold 10)
  const lowStockThreshold = 10;
  const lowStock: LowStockItem[] = Array.from({ length: 8 + Math.floor(rand() * 6) }).map((_, i) => ({
    variant: `Variant ${String.fromCharCode(65 + (i % 26))}/${1 + (i % 4)}`,
    available: 1 + Math.floor(rand() * lowStockThreshold),
  }));
  const outOfStockSkus = Math.floor(rand() * 7);
  const doh: DohItem[] = Array.from({ length: 6 }).map((_, i) => ({ sku: `SKU-${100 + i}`, days: 12 + Math.floor(rand() * 40) }));
  const deadStock: DeadStockItem[] = Array.from({ length: 4 }).map((_, i) => ({ sku: `SKU-${200 + i}`, days: 65 + Math.floor(rand() * 120) }));

  // Fulfillment & Ops
  const avgFulfillmentHours = Math.round(36 + rand() * 100);
  const onTimeFulfillmentRate = avgFulfillmentHours <= 96 ? +(0.85 + rand() * 0.13).toFixed(4) : +(0.5 + rand() * 0.3).toFixed(4);
  const shippingMargin = +(0.12 + rand() * 0.16).toFixed(4);

  return {
    gmv: +gmv.toFixed(2),
    ordersCount,
    salesSeries,
    netSales,
    avgOrderValue,
    avgItemsPerOrder,
    bestSellers,
    salesByChannel,
    salesByCategory,
    statusBreakdown,
    cancellationsCount,
    cancellationsRate,
    voucherImpact,
    giftCardImpact,
    topCustomers,
    repeatRate,
    timeToSecondOrderDays,
    churnRiskRate,
    lowStockThreshold,
    lowStock,
    outOfStockSkus,
    doh,
    deadStock,
    avgFulfillmentHours,
    onTimeFulfillmentRate,
    refundRate,
    shippingMargin,
    channelPerformance,
    funnel: [],
    segments: { newCustomers: 0, returningCustomers: 0, newGmv: 0, returningGmv: 0 },
    cohorts: [],
    ordersHeatmap: Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0)),
    discountByDay: [],
    topDiscountedOrders: [],
    comparison: {
      gmvDelta: null,
      ordersDelta: null,
      aovDelta: null,
      refundRateDelta: null,
      customersDelta: null,
      checkoutsDelta: null,
      priorRange: { from: range.from, to: range.to },
    },
    stuckOrdersCount: 0,
    stuckOrdersThresholdDays: 7,
  };
}

// Helpers
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);

    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function normalizeToOne(values: number[]): number[] {
  const sum = values.reduce((s, v) => s + v, 0) || 1;

  return values.map((v) => v / sum);
}
